import { createElementFromHTML } from './utils'
import EXIF from 'exif-js'

type ImageType = 'image/png' | 'image/jpeg' | 'image/webp'

interface IOptions {
  width?: number
  height?: number
  longSideLength?: number
  type?: ImageType
  encoderOptions?: number
  canvasEl?: HTMLCanvasElement
  toBlob?: boolean
  frameFilters?: Array<(ctx: CanvasRenderingContext2D) => boolean>
  errorTimeout?: number
  errorEvents?: string[]
}

const htmlTemplate = `<canvas style="display:none;"></canvas>`
const defaultLongSideLength = 1280
const defaultType = 'image/jpeg'
const defaultEncoderOptions = 1
const defaultErrorTimeoutOffset = 3000
const defaultErrorEvents = ['error']
let innerCanvasEl: null | HTMLCanvasElement = null
let errorTimeoutTimer: any = null

let resolvedWidth = 0
let resolvedHeight = 0
let originSize = 0

export default function resolve(
  image: HTMLImageElement,
  options: IOptions = {},
): Promise<string[] | Blob[]> {
  const { encoderOptions, toBlob, errorTimeout } = options
  const curCanvas = getCanvasEl(options) as HTMLCanvasElement
  const curType = getType(options)
  const curEncoderOptions = encoderOptions || defaultEncoderOptions
  const curErrorTimeout = errorTimeout || 1000 + defaultErrorTimeoutOffset
  const { resultWidth, resultHeight } = getResultSize(image, options)
  const ctx = curCanvas.getContext('2d') as CanvasRenderingContext2D
  const images: string[] | Blob[] = []

  clearAll(image, options)
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = async () => {
      // console.log('ctx:', ctx)
      if (!ctx) {
        return
      }
      // 解决图片旋转
      EXIF.getData(image as any, async () => {
        const allEXIFTags = EXIF.getAllTags(image)
        // console.log('allEXIFTags:', allEXIFTags)
        // 拍照角度
        const orientation = EXIF.getTag(image, 'Orientation')
        switch (orientation) {
          // // 180 deg
          // case 3:
          //   curCanvas.height = img.height
          //   curCanvas.width = img.width
          //   ctx.rotate((180 * Math.PI) / 180)
          //   ctx.clearRect(0, 0, resultWidth, resultWidth)
          //   ctx.drawImage(image, 0, 0, -curCanvas.width, -curCanvas.height)
          //   break
          // // 顺时针 90 deg
          // case 6:
          //   curCanvas.height = img.width
          //   curCanvas.width = img.height
          //   ctx.rotate((90 * Math.PI) / 180)
          //   ctx.clearRect(0, 0, resultWidth, resultWidth)
          //   ctx.drawImage(image, 0, 0, img.width, -img.height)
          //   break
          // // 逆时针 90 deg
          // case 8:
          //   curCanvas.height = img.width
          //   curCanvas.width = img.height
          //   ctx.rotate((-90 * Math.PI) / 180)
          //   ctx.clearRect(0, 0, resultWidth, resultWidth)
          //   ctx.drawImage(image, 0, 0, -img.width, img.height)
          //   break
          default:
            curCanvas.width = resultWidth
            curCanvas.height = resultHeight
            ctx.clearRect(0, 0, resultWidth, resultWidth)
            ctx.drawImage(image, 0, 0, curCanvas.width, curCanvas.height)
            break
        }
        const dataUrl = curCanvas.toDataURL('image/jpeg')
        let timerOriginal = ''
        if (allEXIFTags && allEXIFTags.DateTimeOriginal) {
          timerOriginal = allEXIFTags.DateTimeOriginal
        }
        document.body.appendChild(curCanvas).setAttribute('timer', timerOriginal)
        document.getElementsByTagName('body')[0].setAttribute('timer', timerOriginal)

        dataURLtoBlob(dataUrl)
        if (toBlob) {
          function handleResolve(blob: Blob) {
            let blobImages = images as Blob[]
            blobImages.push(blob)
            resolve(images)
          }

          async function compress(leftRate: number, rightRate: number) {
            let curRate = (leftRate + rightRate) / 2.0
            let blob = await canvasToBlob(curCanvas, curType, curRate)
            compressCount += 1

            console.log(compressCount, 'curRate:', curRate, 'blob.size:', blob.size)
            if (compressCount >= maxCompressCount) {
              handleResolve(blob)
              return
            }

            if (blob.size > upperBound) {
              await compress(leftRate, curRate)
            } else if (blob.size < lowerBound) {
              await compress(curRate, rightRate)
            } else {
              handleResolve(blob)
            }
          }

          let targetSize = 1024 ** 2
          let lowerBound = targetSize * 0.9
          let upperBound = targetSize * 1.1
          let compressCount = 0
          let maxCompressCount = 5

          let blob = await canvasToBlob(curCanvas, curType, 1)
          console.log('quality 1 compression', blob.size)
          if (blob.size <= upperBound) {  // If quality 1.0 image already smaller than upper bound.
            handleResolve(blob)
          } else {  // Binary search the best quality.
            await compress(0, 1)
          }
        }
      })
    }
    img.src = image.src
    // document.body.appendChild(img)

    addErrorEvents(image, options, event => {
      clearAll(image, options)
      reject('resolve image error: ' + event)
    })
  })
}

function addErrorEvents(
  image: HTMLImageElement,
  options: IOptions,
  callback: (event: string) => void,
) {
  const { errorEvents } = options
  const events = errorEvents || defaultErrorEvents

  events.forEach((_: any) => {
    ; (image as any)['on' + _] = () => {
      callback(_)
    }
  })
}

function getCanvasEl(options: IOptions) {
  const { canvasEl } = options
  if (canvasEl) {
    return canvasEl
  }
  if (!canvasEl) {
    innerCanvasEl = createElementFromHTML<HTMLCanvasElement>(htmlTemplate)
    document.body.appendChild(innerCanvasEl)
  }
  return innerCanvasEl
}

function getType(options: IOptions) {
  const { type } = options
  const supportTypes = getSupportImageTypes()
  if (!type || !supportTypes.includes(type)) {
    return defaultType
  }
  return type
}

export function getSupportImageTypes(
  options: {
    canvasEl?: HTMLCanvasElement,
  } = {},
): ImageType[] {
  const types: ImageType[] = ['image/png', 'image/jpeg']
  const canvasEl = getCanvasEl(options) as HTMLCanvasElement
  if (canvasEl.toDataURL('image/webp').includes('data:image/webp')) {
    types.push('image/webp')
  }
  return types
}

function getResultSize(image: HTMLImageElement, options: IOptions) {
  const { width, height, longSideLength } = options
  const curLongSideLength = longSideLength || defaultLongSideLength
  let resultWidth = 0
  let resultWidthRatio = 0
  let resultHeightRatio = 0
  let resultHeight = 0

  // scale as width
  if (width && !height) {
    resultWidth = Math.min(width, image.width)
    resultWidthRatio = resultWidth / image.width
    resultHeightRatio = resultHeight / image.height
    resolvedWidth = resultWidth
    resolvedHeight = resultHeight
    return { resultWidth, resultHeight }
  }
  // scale as height
  if (!width && height) {
    resultHeight = Math.min(height, image.height)
    resultHeightRatio = resultHeight / image.height
    resultWidth = resultHeightRatio * image.height
    resolvedWidth = resultWidth
    resolvedHeight = resultHeight
    return { resultWidth, resultHeight }
  }
  // scale as longSideLength by default
  if (image.width >= image.height) {
    resultWidth = Math.min(curLongSideLength, image.width)
    resultWidthRatio = resultWidth / image.width
    resultHeight = resultWidthRatio * image.height
    resolvedWidth = resultWidth
    resolvedHeight = resultHeight
    return { resultWidth, resultHeight }
  }
  resultHeight = Math.min(curLongSideLength, image.height)
  resultHeightRatio = resultHeight / image.height
  resultWidth = resultHeightRatio * image.width
  resolvedWidth = resultWidth
  resolvedHeight = resultHeight
  return { resultWidth, resultHeight }
}

function clearAll(image: HTMLImageElement, options: IOptions) {
  clearErrorEvents(image, options)
  clearErrorTimeoutTimer()
}

function clearErrorEvents(image: HTMLImageElement, options: IOptions) {
  const { errorEvents } = options
  const events = errorEvents
    ? [...defaultErrorEvents, ...errorEvents]
    : [...defaultErrorEvents]
  events.forEach(_ => {
    ; (image as any)['on' + _] = null
  })
}

function clearErrorTimeoutTimer() {
  clearTimeout(errorTimeoutTimer)
  errorTimeoutTimer = null
}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  type: ImageType,
  encoderOptions: number,
): Promise<Blob> {
  if (!HTMLCanvasElement.prototype.toBlob) {
    Object.defineProperty(HTMLCanvasElement.prototype, 'toBlob', {
      value(callback: any, type: any, quality: any) {
        const canvas = this;
        setTimeout(() => {
          const binStr = atob(canvas.toDataURL(type, quality).split(',')[1]);
          const len = binStr.length;
          const arr = new Uint8Array(len);
          for (let i = 0; i < len; i++) {
            arr[i] = binStr.charCodeAt(i);
          }
          callback(new Blob([arr], { type: type || 'image/jpeg' }));
        });
      },
    });
  }
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      blob => {
        if (blob) {
          resolve(blob)
        }
      },
      type,
      encoderOptions,
    )
  })
}

export function toImagesBlob(image: HTMLImageElement, options: IOptions, size: number) {
  options.toBlob = true
  originSize = size
  return resolve(image, options) as any
}

interface IAnalyzeImagesResult {
  type: 'base64' | 'blob'
  dataType: string
  length: number
  size: number,
  resolvedWidth: number,
  resolvedHeight: number,
}

export function analyzeImages(images: string[] | Blob[]): IAnalyzeImagesResult {
  const image = images[0]

  // console.log('analyzeImages', (images as Blob[]).map(_ => _.size).reduce((val, cur) => val + cur, 0))
  if (!image) {
    throw new Error('Invalid images')
  }
  if (typeof image === 'string') {
    const regexResult = image.match(/data:(.*);base64/)
    return {
      type: 'base64',
      dataType: regexResult && regexResult[1] ? regexResult[1] : '',
      length: images.length,
      size:
        (images as string[]).map((_: any) => _.slice(23)).join('').length / 1024,
      // tslint:disable-next-line
      resolvedWidth: resolvedWidth,
      // tslint:disable-next-line
      resolvedHeight: resolvedHeight,
    }
  }
  return {
    type: 'blob',
    dataType: image.type,
    length: images.length,
    size:
      (images as Blob[]).map(_ => _.size).reduce((val, cur) => val + cur, 0) /
      1024,
    // tslint:disable-next-line
    resolvedWidth: resolvedWidth,
    // tslint:disable-next-line
    resolvedHeight: resolvedHeight,
  }
}

function dataURLtoBlob(dataUrl: any) {
  const arr = dataUrl.split(',')
  const mime = arr[0].match(/:(.*?);/)[1]
  const bstr = atob(arr[1])
  let n = bstr.length
  const u8arr = new Uint8Array(n)
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n)
  }
  return new Blob([u8arr], { type: mime })
}


function downloadHandler(image: any) {
  const a = document.createElement('a')
  const url = window.URL.createObjectURL(image)
  a.href = url
  a.download = image.name
  a.click()
  window.URL.revokeObjectURL(url)
}


